<template>
  <div v-if="domain && survey"
       :class="['survey-response', { pdf: isPdf }]"
       class="survey-detail">
    <div class="survey-detail-wrap">
      <div class="detail-header"
           v-if="!isPdf"
           :style="{ height: `${98 - scrollTop}px` }"
           :class="{ scrolled: scrollTop > 0 }">
        <div class="detail-button">
          <ui-button :iconName="'icon--arrow-left-black'"
                     @click="toDashboard">&nbsp;&nbsp;{{ $t('common.list') }}&nbsp;&nbsp;</ui-button>
        </div>

        <div class="detail-meta">
          <div class="detail-meta-wrap">
            <div class="detail-tab"
                 :class="{ answer: show === 'answer' }">
              <button :class="{ active: show === 'summary' }"
                      @click="changeShowing('summary')">{{ $t('userResearch.survey.detail.seeSummary') }}</button>
              <button :class="{ active: show === 'answer' }"
                      @click="changeShowing('answer')">{{ $t('userResearch.survey.detail.seeAnswers') }}</button>
            </div>
            <div class="buttons">
              <div v-if="[SURVEY_CONSTANT.STATUS_RUNNING, SURVEY_CONSTANT.STATUS_STOP, SURVEY_CONSTANT.STATUS_WAITING].includes(survey.status)"
                   class="status">
                <span>
                  {{ $t('userResearch.survey.column.status') }}
                </span>
                <span class="switch"
                      @click="updateStatus">
                  <ui-tooltip v-if="survey.status === SURVEY_CONSTANT.STATUS_STOP && hasRunning"
                              arrowCenter
                              arrowBottom>
                    <span v-html="$t('userResearch.survey.onlyOne')"></span>
                  </ui-tooltip>
                  <ba-switch :value="survey.status !== SURVEY_CONSTANT.STATUS_STOP"
                             :disabled="(survey.status === SURVEY_CONSTANT.STATUS_STOP && hasRunning) || isDemo"
                             :default="false"
                             :offColor="'#d7d7d7'"
                             :onColor="'#64b950'"
                             :offLabel="'Stop'"
                             :onLabel="'ON'"
                             :width="56" />
                </span>
              </div>
              <div class="more">
                <i class="icon icon--hamburger"></i>
                <ul class="more-options">
                  <li v-if="survey.status !== 'end'">
                    <button @click="editSurvey" :disabled="isDemo" :class="{disabled: isDemo}">
                      {{$t('common.edit')}}
                    </button>
                  </li>
                  <li>
                    <button @click="deleteSurvey" :disabled="isDemo" :class="{disabled: isDemo}">
                      {{$t('common.delete')}}
                    </button>
                  </li>
                  <li>
                    <button @click="duplicateSurvey" :disabled="isDemo" :class="{disabled: isDemo}">
                      {{ $t('group.editMode.duplicate') }}
                    </button>
                  </li>
                </ul>
              </div>
              <div class="registration">
                <ui-button :size="'large'"
                           :primary2="true"
                           :disabled="isDemo"
                           @click="go({ name: 'addSurvey', query: { sid: $route.query.sid } })">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                    <g fill="#fff" fill-rule="evenodd" transform="translate(2 2)">
                      <rect width="12" height="2" y="5" rx="1"/>
                      <rect width="12" height="2" y="5" rx="1" transform="rotate(90 6 6)"/>
                    </g>
                  </svg>
                  {{ $t('userResearch.survey.createOne') }}
                </ui-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="detail-body"
           :style="{ height: `calc(100vh - ${176 - scrollTop}px)` }"
           @scroll="onScroll">
        <div class="detail-body-wrap">
          <transition name="fade">
            <div v-if="loading"
                 class="loader">
              <ba-loading :applyBackground="true"
                          :showLoadingText="true"/>
            </div>
          </transition>

          <survey-summary v-if="showing === 'summary'"
                          :survey="survey"
                          :timezone="domain.timezone"
                          :timezoneName="domain.timezoneName"
                          @loaded="loading = false" />

          <survey-response v-if="showing === 'answer'"
                           :survey="survey"
                           :domain="domain"
                           :startDate="startDate"
                           :endDate="endDate"
                           :isPdf="isPdf"
                           :timezone="domain.timezone"
                           :timezoneName="domain.timezoneName"
                           @changeStart="startDate = $event"
                           @changeEnd="endDate = $event"
                           @onDownload="isExcelDownloading = $event"
                           @loaded="loading = false" />
        </div>
      </div>
    </div>

    <dimmed v-if="deletingSurvey">
      <alert :title="deletingSurvey.status === 'preview' ? $t('userResearch.survey.alert.delete.previewTitle') : $t('userResearch.survey.alert.delete.title')"
             :description="deletingSurvey.status === 'preview' ? '' : $t('userResearch.survey.alert.delete.description')"
             :icon="'icon--trash-large'"
             :submit="$t('common.delete')"
             :cancel="$t('common.cancel')"
             @onSubmit="confirmDeleteSurvey"
             @onCancel="deletingSurvey = null" />
    </dimmed>

    <dimmed v-if="isRunningSurvey">
      <alert :title="$t('userResearch.survey.alert.run.title')"
             :description="$t('userResearch.survey.alert.run.description')"
             :icon="'icon--dialog-warning'"
             :submit="$t('common.confirm')"
             @onSubmit="isRunningSurvey = false"
      />
    </dimmed>

    <dimmed v-if="isExcelDownloading">
      <alert :title="$t('userResearch.survey.alert.excel.title')"
             :description="$t('userResearch.survey.alert.excel.description')"
             :icon="'icon--dialog-warning'"
             :submit="$t('common.confirm')"
             @onSubmit="isExcelDownloading = false"
      />
    </dimmed>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { EventBus } from '@/libs/event-bus'
import { getData } from '@/api/domain'
import { camelize, utcToLocalTimezone } from '@/libs/utils'
import { getSurvey, getSurveyList, updateSurveyStatus, deleteSurvey } from '@/api/survey'

import SurveySummary from '@/components/user-research/survey/Summary'
import SurveyResponse from '@/components/user-research/survey/Response'

import UiTooltip from '@/components/ui/UiTooltip'
import UiButton from '@/components/ui/UiButton'
import BaSwitch from '@/components/ui/ba/Switch'
import BaLoading from '@/components/ui/ba/Loading'
import Dimmed from '@/components/ui/Dimmed'
import Alert from '@/components/ui/Alert'
import { format, parse, min, max, addDays } from 'date-fns'
import { SURVEY_CONSTANT } from '@/libs/constants'

export default {
  name: 'SurveyDetail',
  components: {
    SurveySummary,
    SurveyResponse,
    UiTooltip,
    UiButton,
    BaSwitch,
    BaLoading,
    Dimmed,
    Alert
  },
  created () {
    EventBus.$emit('setTitle', this.$t('userResearch.survey.title'))

    let sid = this.$route.query.sid

    getData(sid).then(result => {
      this.domain = {
        sid: result.orgSid || result.sid,
        orgSid: result.orgSid,
        realUrl: result.realUrl || result.domain,
        domain: result.domain,
        domainName: result.domainName,
        timezone: result.timezone,
        timezoneName: result.timezoneName
      }

      getSurveyList({ sid }, true).then(result => {
        this.afterLoad(result)
        this.setSurveyList([sid, result])
      })
      // if (!this.surveyList || !this.surveyList[sid]) {
      //   getSurveyList({ sid }, true).then(result => {
      //     this.afterLoad(result)
      //     this.setSurveyList([sid, result])
      //   })
      // } else {
      //   this.afterLoad(this.surveyList[sid])
      // }
    })

    if (this.$route.query.pdf) {
      this.showing = 'answer'
    }
  },
  mounted () {
    if (this.$route.query.pdf) {
      document.getElementById('dashboardHeader').style.display = 'none'
      document.getElementById('dashboardLayout').style.width = '1050px'
      document.getElementById('dashboardLayout').style.minWidth = '10px'
      document.getElementById('dashboardLayout').style.margin = '0 auto'
      document.getElementById('dashboardLayout').style.position = 'static'
      this.isPdf = true
    }
  },
  methods: {
    ...mapMutations([
      'setSurveyList',
      'setSurveyDetail'
    ]),
    afterLoad (result) {
      let sid = this.$route.query.sid
      let surveyId = this.$route.query.survey
      for (const survey of result) {
        if (survey) {
          this.items.push(survey)
        }
      }
      this.calcHasRunning()

      let key = `${sid}-${surveyId}`
      getSurvey({
        sid,
        surveyId
      }, true).then((r) => {
        this.afterLoadSurvey(r)
        this.setSurveyDetail([key, r])
      })
      // if (!this.surveyDetail[key]) {
      //   getSurvey({
      //     sid,
      //     surveyId
      //   }).then((r) => {
      //     this.afterLoadSurvey(r)
      //     this.setSurveyDetail([key, r])
      //   })
      // } else {
      //   this.afterLoadSurvey(this.surveyDetail[key])
      // }
    },
    afterLoadSurvey (result) {
      this.survey = result
      this.survey.questions.sort((a, b) => a.questionOrder - b.questionOrder)
      EventBus.$emit('onLoading', false)

      let endDate = min(utcToLocalTimezone(new Date(), this.domain.timezoneName), parse(this.survey.endDate))
      let startDate = max(addDays(endDate, -7), parse(this.survey.startDate))
      endDate = max(endDate, startDate)
      this.endDate = format(parse(this.$route.query.end || endDate), 'YYYY-MM-DD')
      this.startDate = format(parse(this.$route.query.start || startDate), 'YYYY-MM-DD')
    },
    toDashboard () {
      EventBus.$emit('onLoading', true)
      setTimeout(() => {
        this.$router.push({ name: 'userResearch', query: { sid: this.$route.query.sid } })
      }, 300)
    },
    go (params) {
      EventBus.$emit('onLoading', true)
      setTimeout(() => {
        this.$router.push(params)
      }, 300)
    },
    updateStatus () {
      const survey = this.survey
      if (survey) {
        if (survey.status === SURVEY_CONSTANT.STATUS_RUNNING || survey.status === SURVEY_CONSTANT.STATUS_WAITING || !this.hasRunning) {
          let to = survey.status === SURVEY_CONSTANT.STATUS_STOP ? SURVEY_CONSTANT.STATUS_WAITING : SURVEY_CONSTANT.STATUS_STOP

          updateSurveyStatus({
            sid: this.domain.orgSid,
            surveyId: survey.surveyId,
            status: to
          }, true).then(response => {
            if (response.runningCount) {
              this.isRunningSurvey = true
            } else {
              let surveyList = this.surveyList[this.domain.orgSid]
              for (let i = 0; i < surveyList.length; i++) {
                if (surveyList[i] && surveyList[i].surveyId === survey.surveyId) {
                  surveyList[i].status = to
                  this.$set(survey, 'status', to)
                }
              }
              this.afterLoad(surveyList)
              this.setSurveyList([this.domain.orgSid, surveyList])

              let key = `${this.domain.orgSid}-${survey.surveyId}`
              this.setSurveyDetail([key, {
                ...this.surveyDetail[key],
                status: to
              }])
            }
          })
        }
      }
    },
    calcHasRunning () {
      this.hasRunning = false
      for (const survey of this.items) {
        if ([SURVEY_CONSTANT.STATUS_RUNNING, SURVEY_CONSTANT.STATUS_WAITING].includes(survey.status)) {
          this.hasRunning = true
          break
        }
      }
    },
    editSurvey () {
      const survey = this.survey
      this.go({ name: 'addSurvey', query: { sid: this.domain.sid, survey: survey.surveyId } })
    },
    deleteSurvey () {
      const survey = this.survey
      this.deletingSurvey = survey
    },
    duplicateSurvey () {
      const survey = this.survey
      this.go({ name: 'addSurvey', query: { sid: this.domain.sid, duplicate: survey.surveyId } })
    },
    confirmDeleteSurvey () {
      deleteSurvey({
        sid: this.domain.orgSid,
        surveyId: this.deletingSurvey.surveyId
      }, true).then(() => {
        let surveyList = this.surveyList[this.domain.orgSid]
        for (let i = 0; i < surveyList.length; i++) {
          if (surveyList[i] && surveyList[i].surveyId === this.deletingSurvey.surveyId) {
            delete surveyList[i]
          }
        }
        this.setSurveyList([this.domain.orgSid, surveyList])
        this.toDashboard()
      })
    },
    camelize (str) {
      return camelize(str)
    },
    changeShowing (showing) {
      this.loading = true
      this.show = showing
      setTimeout(() => {
        this.showing = showing
      }, 300)
    },
    onScroll (e) {
      this.scrollTop = e.target.scrollTop
    }
  },
  computed: {
    ...mapState([
      'surveyList',
      'surveyDetail',
      'isDemo'
    ]),
    sid () {
      return this.$route.query.sid
    }
  },
  data () {
    return {
      SURVEY_CONSTANT: SURVEY_CONSTANT,
      loading: true,
      domain: {},
      survey: null,
      items: [],
      deletingSurvey: null,
      isRunningSurvey: false,
      isExcelDownloading: false,
      hasRunning: false,
      showing: 'summary',
      show: 'summary',
      startDate: null,
      endDate: null,
      scrollTop: 0,
      isPdf: false
    }
  }
}
</script>

<style lang="scss" scoped>
.survey-detail {
  .survey-detail-wrap { }

  .detail-header {
    position: relative;
    margin: 20px 0 0;
    height: 98px;
    min-height: 59px;

    &::after {
      content: "";
      display: block;
      position: absolute;
      bottom: -15px;
      left: 0;
      width: 100%;
      height: 15px;
      background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.06), rgba(0, 0, 0, 0));
      border-top: 1px solid #d0d0cd;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &.scrolled {
      &::after {
        opacity: 1;
      }
    }

    .detail-button {
      .ui-btn {
        border: none;
        box-shadow: 0 9px 11px -5px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.08);
        font-size: 13px;
        margin-left: 30px;
        height: 34px;
      }
    }

    .detail-meta {
      margin: 20px 0 0;

      .detail-meta-wrap {
        position: absolute;
        bottom: 0;
        left: calc(50% - 485px);
        width: 970px;
        border-bottom: 1px solid #d7d7d7;
      }

      .detail-tab {
        position: relative;
        display: inline-block;
        padding-top: 7px;

        &::after {
          content: "";
          position: absolute;
          bottom: -1px;
          left: 0;
          width: 163px;
          height: 3px;
          background-color: #ec0047;
          transition: left 0.3s cubic-bezier(0.23, 1, 0.32, 1);
        }

        &.answer {
          &::after {
            left: 163px;
          }
        }

        button {
          width: 163px;
          font-size: 18px;
          font-weight: bold;
          line-height: 1;
          color: #999;
          text-align: center;
          margin: 9px 0;
          transition: color 0.3s ease;

          &.active {
            color: #2f2f2f;
          }
        }
      }

      .buttons {
        float: right;

        .status {
          display: inline-block;
          vertical-align: middle;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.23;
          color: #666;

          span {
            margin-right: 7px;
          }
        }

        .switch {
          position: relative;

          .bubble {
            position: absolute;
            bottom: 28px;
            left: 50%;
            transform: translate(-50%, 20px);
            opacity: 0;
            pointer-events: none;
            transition: transform 0.2s ease,
                        opacity 0.2s ease;

            & /deep/ .text {
              text-align: left;
            }
          }

          &:hover {
            .bubble {
              transform: translate(-50%, 0);
              opacity: 1;
            }
          }
        }

        .more {
          display: inline-block;
          vertical-align: middle;
          position: relative;
          padding-right: 10px;

          .icon {
            cursor: pointer;
            border-radius: 30px;
            padding: 6px;
            background-size: 20px 20px;
          }

          .more-options {
            position: absolute;
            top: 32px;
            left: 50%;
            z-index: 10;
            transform: translate(-50%, -10px) scaleZ(0);
            opacity: 0;
            width: 80px;
            border-radius: 6px;
            box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
            background-color: #fff;
            transform-origin: top center;
            transition: opacity 0.3s ease,
            transform 0.3s ease;

            button {
              width: 100%;
              font-size: 14px;
              padding: 11px 0;
              color: #444;
              background-color: #fff;
              transition: background-color 0.1s ease;

              &:hover {
                background-color: #ebebeb;
              }

              &.disabled {
                background-color: #fff;
                color: #C7C7C7;
                pointer-events: none;
                cursor: default;
              }
            }

            li {
              &:first-child {
                button {
                  border-radius: 7px 7px 0 0;
                }
              }

              &:last-child {
                button {
                  border-radius: 0 0 7px 7px;
                }
              }
            }
          }

          &:hover {
            .icon {
              background-color: #f2f2f2;
            }

            .more-options {
              transform: translate(-50%, 0) scaleZ(1);
              opacity: 1;
            }
          }
        }

        .registration {
          display: inline-block;
          vertical-align: middle;

          .ui-btn {
            & /deep/ svg {
              margin: 3px 4px -3px -4px;
            }

            & /deep/ g {
              transition: fill 0.15s ease;
              fill: #ec0147;
            }

            &:hover {
              & /deep/ g {
                fill: #ffffff;
              }
            }

            &.has {
              & /deep/ g {
                fill: #ffffff;
              }
            }
          }
        }
      }
    }
  }

  .detail-body {
    height: calc(100vh - 176px);
    max-height: calc(100vh - 137px);
    overflow: auto;

    .detail-body-wrap {
      width: 970px;
      margin: 0 auto;
    }

    .loader {
      position: fixed;
      z-index: 100;
      top: 200px;
      left: 0;
      height: calc(100vh - 200px);
      width: 100%;
      background-color: #f4f4f1;

      &.fade-enter-active, &.fade-leave-active {
        opacity: 1;
        transition: opacity 0.2s ease;
      }

      &.fade-enter, &.fade-leave-to{
        opacity: 0;
      }
    }
  }

  &.pdf {
    .detail-header {
      display: none;
    }

    .detail-body {
      height: auto !important;
      max-height: none;

      .detail-body-wrap {
        width: auto;
      }
    }
  }
}
</style>
